import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button, CircularProgress, Input } from '@mui/material';
import { darken } from 'polished';
import { ReactComponent as UploadIconSrc } from 'src/assets/icons/download.svg';
import styled from 'styled-components';

import { CsvUploadResponse, uploadCsvForImport } from '@/features/canvas/api/import';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useShowToast } from '@/hooks/useShowToast';

interface CsvUploadButtonProps {
  onUploadComplete: (data: CsvUploadResponse) => void;
}

const CsvUploadButton = ({ onUploadComplete }: CsvUploadButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const { roadmap } = useEditorContext();
  const [isActive, setIsActive] = useState(false);

  const { mutate: uploadCsv, isLoading } = useMutation(
    (file: File) => uploadCsvForImport(roadmap.id!, file),
    {
      onSuccess: data => {
        showToast('success', t('editor.sidebar.data_tab.csv_upload_success'));
        console.log('CSV Upload Response:', data);
        onUploadComplete(data);
        setIsActive(false);

        // Clear the input value to allow re-uploading the same file
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      onError: (error: unknown) => {
        console.error('CSV Upload Error:', error);
        const errorMessage =
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          t('editor.sidebar.data_tab.csv_upload_error');
        showToast('error', errorMessage);
        setIsActive(false);

        // Clear the input value
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
    }
  );

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension !== 'csv') {
        showToast('error', t('editor.sidebar.data_tab.invalid_file_type'));
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        showToast('error', t('editor.sidebar.data_tab.file_too_large'));
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
        return;
      }

      setIsActive(true);
      uploadCsv(file);
    }
  };

  const handleButtonClick = () => {
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <ContentWrapper>
      <Input
        type="file"
        inputRef={fileInputRef}
        hidden
        inputProps={{ accept: '.csv,text/csv' }}
        onChange={handleFileInputChange}
        disabled={isLoading}
      />
      <UploadButton
        $isActive={isActive || isLoading}
        type="button"
        startIcon={
          isLoading ? <CircularProgress size={20} /> : <UploadIconSrc width="2rem" height="2rem" />
        }
        onClick={handleButtonClick}
        disabled={isLoading}
      >
        {isLoading
          ? t('editor.sidebar.data_tab.uploading_csv')
          : t('editor.sidebar.data_tab.upload_csv_file_server')}
      </UploadButton>
    </ContentWrapper>
  );
};

export { CsvUploadButton };

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const UploadButton = styled(Button)<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 2rem;
  padding: 0.25rem 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows[0]};
  font-size: 1.75rem;
  font-weight: 600;
  text-transform: none;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.palette.brand.white : theme.palette.brand.textPrimary};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.palette.brand.editorTabPrimary : theme.palette.brand.white};
  gap: 0.5rem !important;
  justify-content: center !important;

  svg {
    path {
      fill: ${({ theme, $isActive }) =>
        $isActive ? theme.palette.brand.white : theme.palette.brand.textPrimary};
    }
  }

  :hover:not(:disabled) {
    background-color: ${({ theme, $isActive }) =>
      $isActive
        ? darken(0.1, theme.palette.brand.editorTabPrimary)
        : darken(0.1, theme.palette.brand.white)};
  }

  :disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .MuiCircularProgress-root {
    color: ${({ theme }) => theme.palette.brand.white};
  }
`;
